import * as d3 from 'd3';
import { LayoutEdge, LayoutNode } from '../graph/types';
import { ThemeColors } from './theme';

const ARROW_ID = 'arrow-head';

export function defineArrowMarker(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  colors: ThemeColors
): void {
  const defs = svg.selectAll('defs').data([null]).join('defs');
  defs
    .selectAll(`marker#${ARROW_ID}`)
    .data([null])
    .join('marker')
    .attr('id', ARROW_ID)
    .attr('viewBox', '0 -5 10 10')
    .attr('refX', 10)
    .attr('refY', 0)
    .attr('markerWidth', 8)
    .attr('markerHeight', 8)
    .attr('orient', 'auto')
    .append('path')
    .attr('d', 'M0,-5L10,0L0,5')
    .attr('fill', colors.edgeStroke);
}

export function renderEdges(
  container: d3.Selection<SVGGElement, unknown, null, undefined>,
  edges: LayoutEdge[],
  nodeMap: Map<string, LayoutNode>,
  colors: ThemeColors
): void {
  const line = d3
    .line<{ x: number; y: number }>()
    .x((d) => d.x)
    .y((d) => d.y)
    .curve(d3.curveBasis);

  container
    .selectAll<SVGPathElement, LayoutEdge>('path.edge')
    .data(edges, (d: LayoutEdge) => `${d.from}->${d.to}`)
    .join('path')
    .attr('class', 'edge')
    .attr('d', (d) => {
      const points = clipToNodeBounds(d, nodeMap);
      return points.length > 0 ? line(points) ?? '' : '';
    })
    .attr('fill', 'none')
    .attr('stroke', colors.edgeStroke)
    .attr('stroke-width', 1.5)
    .attr('marker-end', `url(#${ARROW_ID})`);
}

function clipToNodeBounds(
  edge: LayoutEdge,
  nodeMap: Map<string, LayoutNode>
): { x: number; y: number }[] {
  if (edge.points.length === 0) return [];
  const source = nodeMap.get(edge.from);
  const target = nodeMap.get(edge.to);
  if (!source || !target) return edge.points;

  const points = [...edge.points];
  points[0] = closestBoundaryPoint(points[0], points[1], source);
  points[points.length - 1] = closestBoundaryPoint(
    points[points.length - 1],
    points[points.length - 2],
    target
  );
  return points;
}

function closestBoundaryPoint(
  outside: { x: number; y: number },
  inside: { x: number; y: number },
  node: LayoutNode
): { x: number; y: number } {
  const halfW = node.width / 2;
  const halfH = node.height / 2;
  const dx = inside.x - outside.x;
  const dy = inside.y - outside.y;

  if (dx === 0 && dy === 0) return outside;

  const scaleX = halfW / Math.max(Math.abs(dx), 1e-6);
  const scaleY = halfH / Math.max(Math.abs(dy), 1e-6);
  const scale = Math.min(scaleX, scaleY);

  return {
    x: node.x + dx * scale,
    y: node.y + dy * scale
  };
}
